import { useCallback, useState } from 'react'
import { showToastMessage } from 'common/ToastMessage/ShowToastMessage'
import cx from 'classnames'

import Styles from './InviteLink.module.scss'
import SettingsStyles from 'components/Settings/SettingsModal.module.scss'
import { ActionableInput } from 'components/Settings/components/ActionableInput/ActionableInput'
import ToggleButton from 'common/ToggleButton/ToggleButton'
import { ReactComponent as CopyLinkIcon } from 'assets/icons/copyLink16.svg'
import { ReactComponent as RegenerateLinkIcon } from 'assets/icons/refresh16.svg'
import { Workspace } from 'types'
import { SUCCESS_RESPONSE_TYPE } from 'api/constants'
import client from 'api/client'
import { frontendOrigin } from 'utils/frontendOrigin'
import { Heading } from 'blaze-ui/Heading/Heading'
import { Text } from 'blaze-ui/Text/Text'
interface Props {
  workspace: Workspace
}

export const InviteLink = ({ workspace }: Props) => {
  const [inviteLinkEnabled, setInviteLinkEnabled] = useState<boolean>(workspace.invitation_token_enabled)

  const generateInviteLink = useCallback(
    (inviteToken: any) => `${frontendOrigin()}/invite/workspace/${inviteToken}`,
    [],
  )

  const [inviteLink, setInviteLink] = useState<string>(generateInviteLink(workspace.invitation_token))

  const copyLink = useCallback(() => {
    navigator.clipboard.writeText(inviteLink)
    showToastMessage('Invite link copied to your clipboard', 'success')
  }, [inviteLink])

  const regenerateLink = useCallback(async () => {
    const res = await client.regenerateInvitationToken(workspace.key)
    if (res.type !== SUCCESS_RESPONSE_TYPE) {
      showToastMessage('Something went wrong. Please try again', 'error')
    } else {
      workspace.invitation_token = res.data.invitation_token
      setInviteLink(generateInviteLink(res.data.invitation_token))
      showToastMessage('Invite link regenerated', 'success')
    }
  }, [generateInviteLink, workspace])

  /**
   * Handle saving domains
   */
  const onToggle = useCallback(async () => {
    if (!workspace) return

    setInviteLinkEnabled((prev) => !prev)

    const res = await client.updateWorkspaceSettings(
      {
        workspace: {
          invitation_token_enabled: !inviteLinkEnabled,
        },
      },
      workspace.key,
    )

    if (res.type !== SUCCESS_RESPONSE_TYPE) {
      setInviteLinkEnabled((prev) => !prev)
      showToastMessage('Something went wrong. Please try again', 'error')
    } else {
      workspace.invitation_token_enabled = !inviteLinkEnabled
      workspace.invitation_token = res.data.company.invitation_token
      setInviteLink(generateInviteLink(workspace.invitation_token))
    }
  }, [inviteLinkEnabled, workspace, generateInviteLink])

  if (workspace.is_ap_freemium) {
    return <></>
  }

  return (
    <div className={cx(SettingsStyles.settingsSection, Styles.container)}>
      <div className={SettingsStyles.settingsOption}>
        <Heading level={5}>Invite share link</Heading>
        <div className={SettingsStyles.helpText}>
          <Text variant="secondary" color="var(--dark-60)">
            Enable this link for non-admin members to invite new members to the workspace. Invite links are
            automatically regenerated every {workspace.invitation_token_expiration_days} days.
          </Text>

          <div className={SettingsStyles.right}>
            <ToggleButton name="enableInviteLink" checked={inviteLinkEnabled} handleChange={onToggle} />
          </div>
        </div>
        <ActionableInput
          label="Invite Link"
          withLabel={false}
          isDisabled={!inviteLinkEnabled}
          value={inviteLink}
          actions={[
            { title: 'Regenerate Link', onClick: regenerateLink, icon: RegenerateLinkIcon },
            { title: 'Copy Link', onClick: copyLink, icon: CopyLinkIcon },
          ]}
        />
      </div>
    </div>
  )
}
